import { useMemo, useState } from 'react'

type Step =
  | 'tipo_solicitacao'
  | 'dados_imovel'
  | 'documentacao'
  | 'debito_contrato'
  | 'agua_linha_azul'
  | 'agua_levantamento_retorno'
  | 'agua_extensao_limite'
  | 'agua_valor_extensao'
  | 'agua_pavimento_extensao'
  | 'agua_ramal_espera'
  | 'agua_pavimento_normal'
  | 'esgoto_linha_laranja'
  | 'esgoto_interesse_rede_disponivel'
  | 'esgoto_linha_amarela'
  | 'esgoto_interesse_levantamento'
  | 'esgoto_levantamento_retorno'
  | 'esgoto_extensao_limite'
  | 'esgoto_valor_extensao'
  | 'esgoto_pavimento_extensao'
  | 'esgoto_cliente_possui_agua'
  | 'esgoto_rede_liberada'
  | 'esgoto_til_compartilhado'
  | 'esgoto_economias_confirmadas'
  | 'esgoto_economias_faixa'
  | 'esgoto_novo_til_pavimento'
  | 'esgoto_condicao_local'
  | 'resumo_final'

type FlowStatus = 'concluido' | 'aguardando' | 'pendencia' | 'potencial' | 'agua_sem_esgoto'

type Ordem = {
  area: 'Água' | 'Esgoto'
  codigo: string
  assunto: string
  observacao?: string
}

type AreaState = {
  resultado: string
  levantamento: string
  pavimento: string
}

type FlowState = {
  tipoSolicitacao: string
  dadosImovel: string
  documentacao: string
  debitoContrato: string
  fluxoAjustado: string
  agua: AreaState
  esgoto: AreaState
  ordens: Ordem[]
  acoes: string[]
  pendencias: string[]
  observacoes: string[]
  historico: { step: Step; answer: string }[]
  status: FlowStatus | ''
}

type Question = {
  title: string
  helper?: string
  options: string[]
  arcgis?: boolean
}

const ARCGIS_URL = 'https://arcgis.aegea.com.br/portal/apps/webappviewer/index.html?id=0cbbe90bebaf4d7a85d07c7af12b0de0'

const initialState: FlowState = {
  tipoSolicitacao: '',
  dadosImovel: '',
  documentacao: '',
  debitoContrato: '',
  fluxoAjustado: '',
  agua: { resultado: '', levantamento: '', pavimento: '' },
  esgoto: { resultado: '', levantamento: '', pavimento: '' },
  ordens: [],
  acoes: [],
  pendencias: [],
  observacoes: [],
  historico: [],
  status: '',
}

const STATUS_LABELS: Record<FlowStatus, string> = {
  concluido: 'Orientação concluída',
  aguardando: 'Aguardando retorno do levantamento',
  pendencia: 'Não prosseguir até regularização',
  potencial: 'Sem viabilidade imediata — Potencial de Esgoto',
  agua_sem_esgoto: 'Água orientada; esgoto verificado/ofertado e não contratado',
}

function addUnique(list: string[], value: string) {
  if (!value || list.includes(value)) return list
  return [...list, value]
}

function addOrder(list: Ordem[], order: Ordem) {
  if (list.some((item) => item.codigo === order.codigo)) return list
  return [...list, order]
}

function cloneState(state: FlowState): FlowState {
  return {
    ...state,
    agua: { ...state.agua },
    esgoto: { ...state.esgoto },
    ordens: [...state.ordens],
    acoes: [...state.acoes],
    pendencias: [...state.pendencias],
    observacoes: [...state.observacoes],
    historico: [...state.historico],
  }
}

function questionFor(step: Step, state: FlowState): Question | null {
  switch (step) {
    case 'tipo_solicitacao':
      return {
        title: 'Qual é a necessidade apresentada pelo cliente?',
        helper: 'O flow contempla somente água, somente esgoto ou implantação conjunta.',
        options: ['Somente água', 'Somente esgoto', 'Água e esgoto'],
      }
    case 'dados_imovel':
      return { title: 'O operador já recebeu todos os dados do imóvel: rua, número, bairro, quadra e lote?', options: ['Sim', 'Não'] }
    case 'documentacao':
      return {
        title: 'A documentação está completa?',
        helper: 'A versão homologada não possui checklist documental detalhado; esta etapa utiliza a confirmação do operador.',
        options: ['Sim', 'Não'],
      }
    case 'debito_contrato':
      return { title: 'Existe débito ou contrato de prestação de serviços não formalizado?', options: ['Sim', 'Não'] }
    case 'agua_linha_azul':
      return { title: 'Verifique o imóvel no ArcGIS. A linha azul cruza a frente do imóvel?', options: ['Sim', 'Não'], arcgis: true }
    case 'agua_levantamento_retorno':
      return { title: 'A OS 101003 — Levantamento de rede de água já possui retorno?', options: ['Ainda não há retorno', 'Rede disponível', 'Precisa de extensão'] }
    case 'agua_extensao_limite':
      return { title: 'A extensão de água é de até 40 metros?', options: ['Sim, até 40 m', 'Não, acima de 40 m'] }
    case 'agua_valor_extensao':
      return { title: 'O valor foi informado previamente e o cliente confirmou que deseja prosseguir?', options: ['Sim', 'Não'] }
    case 'agua_pavimento_extensao':
      return { title: 'Qual é o tipo de pavimento para a implantação de água com extensão?', options: ['Asfalto', 'Terra'] }
    case 'agua_ramal_espera':
      return { title: state.agua.levantamento === 'Rede disponível' ? 'O levantamento confirmou rede disponível. Existe ramal de espera no local?' : 'Existe ramal de espera no local?', options: ['Sim', 'Não'] }
    case 'agua_pavimento_normal':
      return { title: 'Qual é o tipo de pavimento no local?', options: ['Asfalto', 'Terra'] }
    case 'esgoto_linha_laranja':
      return {
        title: `${state.tipoSolicitacao === 'Somente água' ? 'Mesmo com pedido inicial somente de água, o processo exige verificar a possibilidade de esgoto. ' : ''}No ArcGIS, a linha laranja cruza a frente do imóvel?`,
        options: ['Sim', 'Não'],
        arcgis: true,
      }
    case 'esgoto_interesse_rede_disponivel':
      return { title: 'A rede de esgoto aparece disponível. Após a oferta, o cliente deseja contratar a implantação de esgoto junto com a ligação de água?', options: ['Sim', 'Não'] }
    case 'esgoto_linha_amarela':
      return { title: 'Existe linha amarela tracejada no ArcGIS?', helper: 'Linha amarela tracejada representa rede ainda não liberada para ligação imediata.', options: ['Sim', 'Não'], arcgis: true }
    case 'esgoto_interesse_levantamento':
      return { title: 'O cliente deseja prosseguir com o levantamento de rede de esgoto após a oferta?', options: ['Sim', 'Não'] }
    case 'esgoto_levantamento_retorno':
      return { title: 'A OS 101006 — Levantamento de rede de esgoto já possui retorno?', options: ['Ainda não há retorno', 'Rede disponível, sem extensão', 'Rede disponível, com extensão', 'Sem rede ou sem viabilidade'] }
    case 'esgoto_extensao_limite':
      return { title: 'A extensão de esgoto é de até 18 metros?', options: ['Sim, até 18 m', 'Não, acima de 18 m'] }
    case 'esgoto_valor_extensao':
      return { title: 'O valor foi informado previamente e o cliente confirmou que deseja prosseguir?', options: ['Sim', 'Não'] }
    case 'esgoto_pavimento_extensao':
      return { title: 'Qual é o tipo de pavimento para a ligação de esgoto com extensão?', options: ['Asfalto', 'Terra'] }
    case 'esgoto_cliente_possui_agua':
      return {
        title: 'O cliente já possui ligação de água?',
        helper: 'Essa informação não deve desviar um pedido “Somente esgoto” para implantação de água.',
        options: ['Sim', 'Não'],
      }
    case 'esgoto_rede_liberada':
      return { title: 'Confirme no ArcGIS: a rede de esgoto está liberada?', options: ['Sim', 'Não'], arcgis: true }
    case 'esgoto_til_compartilhado':
      return { title: 'Existe TIL/terminal de esgoto já pago ou regularizado em imóvel ao lado ou em outra matrícula e que será compartilhado?', options: ['Sim', 'Não'] }
    case 'esgoto_economias_confirmadas':
      return { title: 'O operador já confirmou quantas economias compartilham esse TIL/terminal?', options: ['Sim', 'Não'] }
    case 'esgoto_economias_faixa':
      return { title: 'Quantas economias compartilham o terminal?', options: ['De 1 a 4 economias', 'Mais de 4 economias'] }
    case 'esgoto_novo_til_pavimento':
      return { title: 'Qual é o tipo de pavimento para implantação do novo TIL?', options: ['Asfalto', 'Terra'] }
    case 'esgoto_condicao_local':
      return { title: 'Qual é a condição do local para implantação de esgoto?', options: ['Ramal de espera / TIL já instalado', 'Asfalto', 'Terra'] }
    default:
      return null
  }
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}

function finalOrientation(state: FlowState) {
  if (state.status === 'aguardando') return 'Aguardar o prazo de 10 dias corridos do Setor de Operações e reiniciar o fluxo após receber o retorno do levantamento.'
  if (state.status === 'pendencia' || state.pendencias.length) return 'Não prosseguir até regularizar todas as pendências registradas.'
  if (state.status === 'potencial') return 'Abrir a OS 102001 — Potencial de Esgoto e encerrar a orientação de implantação imediata.'
  if (state.status === 'agua_sem_esgoto') return 'Concluir a orientação da água e registrar que a rede de esgoto foi verificada/ofertada, mas o cliente não prosseguiu com a contratação.'
  return 'Prosseguir com as OS e registros indicados no resumo.'
}

export function ImplantacaoAguaEsgotoFlow() {
  const [step, setStep] = useState<Step>('tipo_solicitacao')
  const [state, setState] = useState<FlowState>(initialState)
  const [message, setMessage] = useState('')

  const question = questionFor(step, state)
  const isSummary = step === 'resumo_final'
  const progress = isSummary ? 100 : Math.min(94, 8 + state.historico.length * 7)
  const waterOrders = state.ordens.filter((item) => item.area === 'Água')
  const sewerOrders = state.ordens.filter((item) => item.area === 'Esgoto')

  const summaryText = useMemo(() => {
    const orderLines = (area: 'Água' | 'Esgoto') => {
      const items = state.ordens.filter((item) => item.area === area)
      return items.length ? items.map((item) => `- OS ${item.codigo} — ${item.assunto}${item.observacao ? ` | ${item.observacao}` : ''}`) : ['- Nenhuma OS indicada para esta etapa.']
    }
    return [
      'RESULTADO — IMPLANTAÇÃO DE ÁGUA/ESGOTO',
      `Status: ${state.status ? STATUS_LABELS[state.status] : '—'}`,
      `Pedido inicial: ${state.tipoSolicitacao || '—'}`,
      state.fluxoAjustado ? `Fluxo ajustado: ${state.fluxoAjustado}` : '',
      `Dados do imóvel: ${state.dadosImovel || '—'}`,
      `Documentação: ${state.documentacao || '—'}`,
      `Débito/contrato: ${state.debitoContrato || '—'}`,
      '',
      `Resultado água: ${state.agua.resultado || 'Não aplicável / ainda não definido.'}`,
      'OS de água:',
      ...orderLines('Água'),
      '',
      `Resultado esgoto: ${state.esgoto.resultado || 'Não aplicável / ainda não definido.'}`,
      'OS de esgoto:',
      ...orderLines('Esgoto'),
      '',
      'Ações obrigatórias:',
      ...(state.acoes.length ? state.acoes.map((item) => `- ${item}`) : ['- Nenhuma ação adicional registrada.']),
      '',
      'Pendências:',
      ...(state.pendencias.length ? state.pendencias.map((item) => `- ${item}`) : ['- Sem pendências registradas.']),
      '',
      'Observações operacionais:',
      ...(state.observacoes.length ? state.observacoes.map((item) => `- ${item}`) : ['- Nenhuma observação adicional.']),
      '',
      'Nota documental: nesta versão, o fluxo utiliza a confirmação do operador de que a documentação está completa.',
      `Orientação final: ${finalOrientation(state)}`,
    ].filter((line) => line !== '').join('\n')
  }, [state])

  function restart() {
    setStep('tipo_solicitacao')
    setState(initialState)
    setMessage('')
  }

  function finish(next: FlowState, status: FlowStatus) {
    next.status = status
    setState(next)
    setStep('resumo_final')
  }

  function addOs(next: FlowState, area: 'Água' | 'Esgoto', codigo: string, assunto: string, observacao = '') {
    next.ordens = addOrder(next.ordens, { area, codigo, assunto, observacao })
  }

  function finishPhysicalSewer(next: FlowState, codigo: string, assunto: string, resultado: string) {
    addOs(next, 'Esgoto', codigo, assunto)
    next.esgoto.resultado = resultado
    next.acoes = addUnique(next.acoes, 'Registrar na OS o endereço de implantação e a localização precisa do TIL.')
    finish(next, 'concluido')
  }

  function enforceSewerOnly(next: FlowState, nextStep: Step): Step {
    if (next.tipoSolicitacao !== 'Somente esgoto' || !nextStep.startsWith('agua_')) return nextStep

    next.agua = { resultado: '', levantamento: '', pavimento: '' }
    next.ordens = next.ordens.filter((item) => item.area !== 'Água')
    next.fluxoAjustado = ''
    next.observacoes = addUnique(next.observacoes, 'Pedido inicial “Somente esgoto”: o fluxo permaneceu restrito às etapas e OS de esgoto.')
    return 'esgoto_rede_liberada'
  }

  function answer(value: string) {
    setMessage('')
    const next = cloneState(state)
    next.historico.push({ step, answer: value })
    let nextStep: Step = step

    switch (step) {
      case 'tipo_solicitacao':
        next.tipoSolicitacao = value
        nextStep = 'dados_imovel'
        break
      case 'dados_imovel':
        next.dadosImovel = value === 'Sim' ? 'Rua, número, bairro, quadra e lote recebidos' : 'Dados incompletos'
        if (value === 'Não') {
          next.pendencias = addUnique(next.pendencias, 'Receber rua, número, bairro, quadra e lote antes de continuar.')
          finish(next, 'pendencia')
          return
        }
        nextStep = 'documentacao'
        break
      case 'documentacao':
        next.documentacao = value === 'Sim' ? 'Confirmada como completa pelo operador' : 'Incompleta'
        if (value === 'Não') {
          next.pendencias = addUnique(next.pendencias, 'Orientar o cliente a regularizar a documentação e aguardar novo contato.')
          finish(next, 'pendencia')
          return
        }
        nextStep = 'debito_contrato'
        break
      case 'debito_contrato':
        next.debitoContrato = value === 'Sim' ? 'Existe pendência de débito e/ou contrato' : 'Sem débito ou contrato não formalizado'
        if (value === 'Sim') {
          next.pendencias = addUnique(next.pendencias, 'Regularizar o débito e formalizar o contrato de prestação de serviços antes de continuar.')
          finish(next, 'pendencia')
          return
        }
        nextStep = next.tipoSolicitacao === 'Somente esgoto' ? 'esgoto_linha_laranja' : 'agua_linha_azul'
        break
      case 'agua_linha_azul':
        if (value === 'Não') {
          addOs(next, 'Água', '101003', 'Levantamento de rede de água', 'Prazo: 10 dias corridos — Setor de Operações.')
          next.acoes = addUnique(next.acoes, 'Abrir a OS 101003 e aguardar 10 dias corridos para retorno do Setor de Operações.')
          next.agua.levantamento = 'OS 101003 necessária'
          nextStep = 'agua_levantamento_retorno'
        } else nextStep = 'agua_ramal_espera'
        break
      case 'agua_levantamento_retorno':
        next.agua.levantamento = value
        if (value === 'Ainda não há retorno') {
          next.agua.resultado = 'Levantamento de rede de água aberto; implantação aguardando retorno.'
          finish(next, 'aguardando')
          return
        }
        nextStep = value === 'Precisa de extensão' ? 'agua_extensao_limite' : 'agua_ramal_espera'
        break
      case 'agua_extensao_limite':
        if (value === 'Não, acima de 40 m') {
          next.acoes = addUnique(next.acoes, 'Informar previamente ao cliente o valor da extensão de água acima de 40 metros.')
          nextStep = 'agua_valor_extensao'
        } else nextStep = 'agua_pavimento_extensao'
        break
      case 'agua_valor_extensao':
        if (value === 'Não') {
          next.pendencias = addUnique(next.pendencias, 'Informar previamente o valor da extensão de água e obter a confirmação do cliente.')
          finish(next, 'pendencia')
          return
        }
        nextStep = 'agua_pavimento_extensao'
        break
      case 'agua_pavimento_extensao':
        next.agua.pavimento = value
        if (value === 'Asfalto') addOs(next, 'Água', '117011', 'Implantação de água em asfalto com extensão')
        else addOs(next, 'Água', '117015', 'Implantação de água em terra com extensão')
        next.agua.resultado = `Implantação de água com extensão definida para pavimento ${value.toLocaleLowerCase('pt-BR')}.`
        next.acoes = addUnique(next.acoes, 'Após definir a ligação de água, verificar obrigatoriamente a rede de esgoto no ArcGIS e ofertar a contratação.')
        nextStep = 'esgoto_linha_laranja'
        break
      case 'agua_ramal_espera':
        if (value === 'Sim') {
          addOs(next, 'Água', '117085', 'Implantação de ligação de água 3/4 — passeio', 'Apenas instalar o hidrômetro.')
          next.agua.resultado = 'Há ramal de espera; utilizar a OS de passeio apenas para instalação do hidrômetro.'
          next.acoes = addUnique(next.acoes, 'Após definir a ligação de água, verificar obrigatoriamente a rede de esgoto no ArcGIS e ofertar a contratação.')
          nextStep = 'esgoto_linha_laranja'
        } else nextStep = 'agua_pavimento_normal'
        break
      case 'agua_pavimento_normal':
        next.agua.pavimento = value
        if (value === 'Asfalto') addOs(next, 'Água', '117065', 'Financiamento de implantação de ligação de água — Asfalto')
        else addOs(next, 'Água', '117007', 'Implantação de ligação de água 3/4 — Terra')
        next.agua.resultado = `Ligação de água sem ramal de espera definida para pavimento ${value.toLocaleLowerCase('pt-BR')}.`
        next.acoes = addUnique(next.acoes, 'Após definir a ligação de água, verificar obrigatoriamente a rede de esgoto no ArcGIS e ofertar a contratação.')
        nextStep = 'esgoto_linha_laranja'
        break
      case 'esgoto_linha_laranja':
        if (value === 'Sim') {
          if (next.tipoSolicitacao === 'Somente água') nextStep = 'esgoto_interesse_rede_disponivel'
          else if (next.tipoSolicitacao === 'Somente esgoto') nextStep = 'esgoto_cliente_possui_agua'
          else {
            next.acoes = addUnique(next.acoes, 'Contratar a ligação de esgoto junto com a ligação de água, quando o atendimento contemplar os dois serviços.')
            nextStep = 'esgoto_til_compartilhado'
          }
        } else nextStep = 'esgoto_linha_amarela'
        break
      case 'esgoto_interesse_rede_disponivel':
        if (value === 'Não') {
          next.esgoto.resultado = 'Rede de esgoto verificada e contratação ofertada; cliente não aderiu.'
          next.observacoes = addUnique(next.observacoes, 'Registrar que a possibilidade de esgoto foi verificada e ofertada após a implantação de água.')
          finish(next, 'agua_sem_esgoto')
          return
        }
        next.acoes = addUnique(next.acoes, 'Contratar a ligação de esgoto junto com a ligação de água, quando o atendimento contemplar os dois serviços.')
        nextStep = 'esgoto_til_compartilhado'
        break
      case 'esgoto_linha_amarela':
        next.observacoes = addUnique(next.observacoes, value === 'Sim'
          ? 'A linha amarela tracejada representa rede de esgoto ainda não liberada; não considerar como ligação imediata.'
          : 'Não foi identificada linha laranja nem linha amarela tracejada no ArcGIS.')
        nextStep = next.tipoSolicitacao === 'Somente água' ? 'esgoto_interesse_levantamento' : 'esgoto_levantamento_retorno'
        if (next.tipoSolicitacao !== 'Somente água') {
          addOs(next, 'Esgoto', '101006', 'Levantamento de rede de esgoto', 'Prazo: 10 dias corridos — Setor de Operações.')
          next.acoes = addUnique(next.acoes, 'Abrir a OS 101006 e aguardar 10 dias corridos para retorno do Setor de Operações.')
          next.esgoto.levantamento = 'OS 101006 necessária'
        }
        break
      case 'esgoto_interesse_levantamento':
        if (value === 'Não') {
          next.esgoto.resultado = 'Rede de esgoto verificada; cliente não desejou prosseguir com levantamento/implantação.'
          next.observacoes = addUnique(next.observacoes, 'Registrar que a possibilidade de esgoto foi verificada e ofertada após a implantação de água.')
          finish(next, 'agua_sem_esgoto')
          return
        }
        addOs(next, 'Esgoto', '101006', 'Levantamento de rede de esgoto', 'Prazo: 10 dias corridos — Setor de Operações.')
        next.acoes = addUnique(next.acoes, 'Abrir a OS 101006 e aguardar 10 dias corridos para retorno do Setor de Operações.')
        next.esgoto.levantamento = 'OS 101006 necessária'
        nextStep = 'esgoto_levantamento_retorno'
        break
      case 'esgoto_levantamento_retorno':
        next.esgoto.levantamento = value
        if (value === 'Ainda não há retorno') {
          next.esgoto.resultado = 'Levantamento de rede de esgoto aberto; implantação aguardando retorno.'
          finish(next, 'aguardando')
          return
        }
        if (value === 'Sem rede ou sem viabilidade') {
          addOs(next, 'Esgoto', '102001', 'Potencial de Esgoto')
          next.esgoto.resultado = 'Levantamento sem rede disponível ou sem viabilidade para implantação imediata.'
          finish(next, 'potencial')
          return
        }
        if (value === 'Rede disponível, com extensão') {
          nextStep = 'esgoto_extensao_limite'
        } else if (next.tipoSolicitacao === 'Somente esgoto') {
          nextStep = 'esgoto_cliente_possui_agua'
        } else {
          next.acoes = addUnique(next.acoes, 'Contratar a ligação de esgoto junto com a ligação de água, quando o atendimento contemplar os dois serviços.')
          nextStep = 'esgoto_til_compartilhado'
        }
        break
      case 'esgoto_extensao_limite':
        if (value === 'Não, acima de 18 m') {
          next.acoes = addUnique(next.acoes, 'Informar previamente ao cliente o valor da extensão de esgoto acima de 18 metros.')
          nextStep = 'esgoto_valor_extensao'
        } else nextStep = 'esgoto_pavimento_extensao'
        break
      case 'esgoto_valor_extensao':
        if (value === 'Não') {
          next.pendencias = addUnique(next.pendencias, 'Informar previamente o valor da extensão de esgoto e obter a confirmação do cliente.')
          finish(next, 'pendencia')
          return
        }
        nextStep = 'esgoto_pavimento_extensao'
        break
      case 'esgoto_pavimento_extensao':
        next.esgoto.pavimento = value
        if (value === 'Asfalto') finishPhysicalSewer(next, '118012', 'Ligação de esgoto em asfalto com extensão', 'Ligação de esgoto com extensão definida para pavimento asfalto.')
        else finishPhysicalSewer(next, '118017', 'Ligação de esgoto em terra com extensão', 'Ligação de esgoto com extensão definida para pavimento terra.')
        return
      case 'esgoto_cliente_possui_agua':
        if (value === 'Não') {
          next.observacoes = addUnique(next.observacoes, 'Cliente não possui ligação de água; manter a solicitação no fluxo de implantação de esgoto, sem gerar OS de água.')
        }
        nextStep = 'esgoto_rede_liberada'
        break
      case 'esgoto_rede_liberada':
        if (value === 'Não') {
          addOs(next, 'Esgoto', '101006', 'Levantamento de rede de esgoto', 'Prazo: 10 dias corridos — Setor de Operações.')
          next.acoes = addUnique(next.acoes, 'Abrir a OS 101006 e aguardar 10 dias corridos para retorno do Setor de Operações.')
          next.esgoto.levantamento = 'OS 101006 necessária'
          nextStep = 'esgoto_levantamento_retorno'
        } else nextStep = 'esgoto_til_compartilhado'
        break
      case 'esgoto_til_compartilhado':
        nextStep = value === 'Sim' ? 'esgoto_economias_confirmadas' : 'esgoto_condicao_local'
        break
      case 'esgoto_economias_confirmadas':
        if (value === 'Não') {
          next.pendencias = addUnique(next.pendencias, 'Confirmar quantas economias compartilham o TIL antes de definir a OS de esgoto.')
          finish(next, 'pendencia')
          return
        }
        nextStep = 'esgoto_economias_faixa'
        break
      case 'esgoto_economias_faixa':
        if (value === 'De 1 a 4 economias') {
          addOs(next, 'Esgoto', '102003', 'Coleta e tratamento de esgoto — Ativação', 'TIL/terminal já pago ou regularizado e compartilhado por no máximo 4 economias.')
          next.esgoto.resultado = 'Utilizar o TIL já pago/regularizado; ativar coleta e tratamento na matrícula atual.'
          next.acoes = addUnique(next.acoes, 'Registrar a matrícula/endereço de origem, a quantidade total de economias e a localização precisa do TIL.')
          finish(next, 'concluido')
          return
        }
        next.observacoes = addUnique(next.observacoes, 'Mais de 4 economias não podem compartilhar o mesmo terminal neste fluxo. A OS 102003 não se aplica; deve ser implantado um novo TIL.')
        nextStep = 'esgoto_novo_til_pavimento'
        break
      case 'esgoto_novo_til_pavimento':
        next.esgoto.pavimento = value
        if (value === 'Asfalto') finishPhysicalSewer(next, '118049', 'Financiamento de implantação de ligação de esgoto — Asfalto', 'Novo TIL obrigatório porque o compartilhamento ultrapassa 4 economias; pavimento asfalto.')
        else finishPhysicalSewer(next, '118007', 'Implantação de ligação de esgoto 100mm — Terra', 'Novo TIL obrigatório porque o compartilhamento ultrapassa 4 economias; pavimento terra.')
        return
      case 'esgoto_condicao_local':
        if (value === 'Ramal de espera / TIL já instalado') finishPhysicalSewer(next, '118050', 'Implantação de ligação de esgoto 100mm — Passeio', 'O local já possui ramal de espera/TIL instalado.')
        else if (value === 'Asfalto') finishPhysicalSewer(next, '118049', 'Financiamento de implantação de ligação de esgoto — Asfalto', 'Não há ramal de espera/TIL instalado; pavimento asfalto.')
        else finishPhysicalSewer(next, '118007', 'Implantação de ligação de esgoto 100mm — Terra', 'Não há ramal de espera/TIL instalado; pavimento terra.')
        return
      default:
        return
    }

    nextStep = enforceSewerOnly(next, nextStep)
    setState(next)
    setStep(nextStep)
  }

  async function copySummary() {
    const ok = await copyText(summaryText)
    setMessage(ok ? 'Resumo copiado.' : 'Não foi possível copiar automaticamente.')
    window.setTimeout(() => setMessage(''), 1800)
  }

  const critical = state.status === 'pendencia' || state.pendencias.length > 0
  const waiting = state.status === 'aguardando'
  const statusClass = critical ? 'danger' : waiting || state.status === 'potencial' || state.status === 'agua_sem_esgoto' ? 'warning' : 'ok'

  return (
    <section className="tit-flow-tool implant-flow-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">FLOW GUIADO</p>
          <h2>Implantação de Água / Esgoto</h2>
          <p className="muted">Oriente implantação, levantamentos, extensões, pavimento, ramal/TIL e terminal compartilhado.</p>
        </div>
        <span className="status-chip">Fluxo determinístico</span>
      </div>

      <section className="tit-progress-card">
        <div className="tit-progress-head">
          <div><strong>{isSummary ? (state.status ? STATUS_LABELS[state.status] : 'Orientação concluída') : 'Orientação em andamento'}</strong><span>{state.tipoSolicitacao || 'Selecione a necessidade inicial'}</span></div>
          <strong>{progress}%</strong>
        </div>
        <div className="tit-progress-track"><div className="tit-progress-bar" style={{ width: `${progress}%` }} /></div>
        <div className="tit-flow-chips">
          {state.tipoSolicitacao && <span>{state.tipoSolicitacao}</span>}
          {waterOrders.length > 0 && <span>Água · {waterOrders.length} OS</span>}
          {sewerOrders.length > 0 && <span>Esgoto · {sewerOrders.length} OS</span>}
          {state.pendencias.length > 0 && <span className="danger">{state.pendencias.length} pendência(s)</span>}
        </div>
      </section>

      {!isSummary && question && (
        <section className="tit-question-card">
          <div className="tit-question-step">ETAPA {state.historico.length + 1}</div>
          <h3>{question.title}</h3>
          {question.helper && <p>{question.helper}</p>}
          {question.arcgis && <a className="primary-button compact implant-arcgis-button" href={ARCGIS_URL} target="_blank" rel="noreferrer">Abrir ArcGIS ↗</a>}
          <div className="tit-question-options">
            {question.options.map((option) => <button type="button" key={option} onClick={() => answer(option)}>{option}</button>)}
          </div>
        </section>
      )}

      {isSummary && (
        <section className="tit-summary-card implant-summary-card">
          <div className="tit-summary-head">
            <div><p className="eyebrow">RESULTADO DA ORIENTAÇÃO</p><h3>{state.status ? STATUS_LABELS[state.status] : 'Resultado'}</h3></div>
            <span className={`tit-result-badge ${statusClass}`}>{critical ? 'NÃO PROSSEGUIR' : waiting ? 'AGUARDAR' : state.status === 'potencial' ? 'POTENCIAL' : 'PODE PROSSEGUIR'}</span>
          </div>

          <div className="tit-summary-grid">
            <div className="tit-summary-block"><span>Pedido inicial</span><strong>{state.tipoSolicitacao || '—'}</strong></div>
            <div className="tit-summary-block"><span>Dados do imóvel</span><strong>{state.dadosImovel || '—'}</strong></div>
            <div className="tit-summary-block"><span>Documentação</span><strong>{state.documentacao || '—'}</strong></div>
            <div className="tit-summary-block"><span>Débito / contrato</span><strong>{state.debitoContrato || '—'}</strong></div>
          </div>

          {state.fluxoAjustado && <div className="tit-summary-alert info"><strong>Fluxo ajustado</strong><p>{state.fluxoAjustado}</p></div>}

          <div className="implant-result-grid">
            <section className="implant-result-area">
              <div className="implant-result-head"><strong>Água</strong><span>{waterOrders.length} OS</span></div>
              <p>{state.agua.resultado || 'Não aplicável / ainda não definido.'}</p>
              <div className="implant-os-list">
                {waterOrders.length ? waterOrders.map((item) => <article key={item.codigo}><strong>OS {item.codigo}</strong><span>{item.assunto}</span>{item.observacao && <small>{item.observacao}</small>}</article>) : <small>Nenhuma OS indicada para esta etapa.</small>}
              </div>
            </section>
            <section className="implant-result-area">
              <div className="implant-result-head"><strong>Esgoto</strong><span>{sewerOrders.length} OS</span></div>
              <p>{state.esgoto.resultado || 'Não aplicável / ainda não definido.'}</p>
              <div className="implant-os-list">
                {sewerOrders.length ? sewerOrders.map((item) => <article key={item.codigo}><strong>OS {item.codigo}</strong><span>{item.assunto}</span>{item.observacao && <small>{item.observacao}</small>}</article>) : <small>Nenhuma OS indicada para esta etapa.</small>}
              </div>
            </section>
          </div>

          <div className={`tit-summary-alert ${state.pendencias.length ? 'danger' : 'ok'}`}>
            <strong>Pendências</strong>
            <ul className="tit-summary-list">{state.pendencias.length ? state.pendencias.map((item) => <li key={item}>{item}</li>) : <li>Sem pendências registradas.</li>}</ul>
          </div>

          <div className="implant-summary-columns">
            <div className="tit-summary-alert info"><strong>Ações obrigatórias</strong><ul className="tit-summary-list">{state.acoes.length ? state.acoes.map((item) => <li key={item}>{item}</li>) : <li>Nenhuma ação adicional registrada.</li>}</ul></div>
            <div className="tit-summary-alert"><strong>Observações operacionais</strong><ul className="tit-summary-list">{state.observacoes.length ? state.observacoes.map((item) => <li key={item}>{item}</li>) : <li>Nenhuma observação adicional.</li>}</ul></div>
          </div>

          <div className="tit-summary-alert warning"><strong>Nota documental</strong><p>Nesta versão, o fluxo utiliza a confirmação do operador de que a documentação está completa. Não há checklist documental detalhado homologado neste flow.</p></div>
          <div className="tit-summary-alert info"><strong>Orientação final</strong><p>{finalOrientation(state)}</p></div>

          <div className="tit-summary-actions">
            <button className="primary-button" type="button" onClick={() => void copySummary()}>Copiar resumo</button>
            <button className="secondary-button" type="button" onClick={restart}>Reiniciar fluxo</button>
          </div>
          {message && <div className="tool-status ok tit-copy-message">{message}</div>}
        </section>
      )}
    </section>
  )
}
